import React, { useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";

export default function DemoAgreement() {
  return (
    <TermsContainer>
      <Helmet>
        <title>Demonstration User Acceptance Terms | SolarAIDD</title>
        <meta name="description" content="Review the User Acceptance Terms for the SolarAIDD AI demonstration. Participation details for CSIR's automated PV defect detection software trial."/>
        <meta name="keywords" content="SolarAIDD demo terms, AI software pilot, PV inspection trial, solar EL analysis demonstration, CSIR technology demonstrator, user acceptance agreement"/>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://solaraidd.csir.co.za/demo-agreement" />

        {/* Structured Data for a WebPage (Specifically an Agreement) */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Demonstration User Acceptance Terms",
              "description": "Terms and conditions for participating in the SolarAIDD AI software demonstration.",
              "provider": {
                "@type": "Organization",
                "name": "CSIR"
              }
            }
          `}
        </script>
      </Helmet>

      <Title>Demonstration User Acceptance Terms</Title>
      {/* <Subtitle>(re: Solar AIDD)</Subtitle> */}

      <Parties>
        between<br />
        <strong>The Council for Scientific and Industrial Research (CSIR)</strong>
        <br /><strong>(Software Owner)</strong>
        <br /><br />
        and<br />
        <strong>You, in your individual or representative capacity as the case may be</strong>
        <br /><strong>(the User)</strong>
      </Parties>

      <SectionTitle>1. Acceptance of Terms</SectionTitle>
      <Paragraph>
        1.1 By clicking the "I Agree" button, you acknowledge that you have read,
        understood, and agree to be bound by the terms and conditions of this
        Demonstration Agreement ("Agreement").
      </Paragraph>
      <Paragraph>
        1.2 If you act in a representative capacity, on behalf of your employer,
        company or any other juristic person, your authority to accept these
        terms is presumed unless otherwise indicated in writing. If you are not
        sure whether you are authorised to accept terms in your representative
        capacity, please satisfy yourself that you are before clicking the “I
        Agree” button.
      </Paragraph>
      <Paragraph>
        1.3 If you do not agree to these terms, you will not be permitted to
        access or use the Demonstration Software.
      </Paragraph>

      <SectionTitle>2. Purpose and Scope of Demo</SectionTitle>
      <Paragraph>
        2.1 This Agreement governs your limited duration access to and use of
        the demonstration version of CSIR's proprietary software for defect
        detection in solar PV module images (the "Demonstration Software").
      </Paragraph>
      <Paragraph>
        2.2 This access is granted for the sole purpose of evaluating the
        functionality and user interface of the Demonstration Software under
        CSIR supervision.
      </Paragraph>
      <Paragraph>
        2.3 Access to the Demonstration Software is provided during a live
        demonstration session. The demonstration session shall not be recorded.
        The Demonstration Software shall not be downloaded, copied, or used for
        any purpose other than the supervised demonstration. Access is time
        limited, and no rights whatsoever are granted beyond the demonstration
        session.
      </Paragraph>

      <SectionTitle>3. Confidential Information</SectionTitle>
      <Paragraph>
        3.1 You acknowledge that the Demonstration Software and all related
        information, including its user interface, features, functionality, and
        any output or results generated, constitutes CSIR Confidential
        Information.
      </Paragraph>
      <Paragraph>
        3.2 You agree to:
        <br />3.2.1 Maintain all CSIR Confidential Information in strict
        confidence.
        <br />3.2.2 Use the CSIR Confidential Information solely for the purpose
        of this demonstration as described in clause 2.
        <br />3.2.3 Take all reasonable measures to prevent any unauthorised
        access to or use of the CSIR Confidential Information.
      </Paragraph>

      <SectionTitle>4. Intellectual Property</SectionTitle>
      <Paragraph>
        4.1 The Demonstration Software and all intellectual property rights,
        whether registered or unregistered, including but not limited to
        algorithms, trade secrets, software, and know-how, are the exclusive
        property of CSIR.
      </Paragraph>
      <Paragraph>
        4.2 This Agreement does not grant you any licence, title, or ownership
        rights to the Demonstration Software or its underlying technology. You
        are not permitted to copy, reproduce, or modify the Demonstration
        Software in any manner whatsoever.
      </Paragraph>

      <SectionTitle>5. Prohibited Actions</SectionTitle>
      <Paragraph>
        5.1 You agree not to engage in any of the following prohibited actions:
        <br />5.1.1 Reverse Engineering: Attempt to decompile, disassemble,
        reverse engineer, or otherwise derive the source code, algorithms, or
        any other underlying technology of the Demonstration Software.
        <br />5.1.2 Unauthorised Access: Access any unauthorised areas of the
        system or attempt to gain control of the software.
        <br />5.1.3 Recording: Record, photograph, or otherwise capture any
        portion of the demonstration session without prior written consent from
        CSIR.
        <br />5.1.4 Competitive Use: Use any information learnt from this
        demonstration to develop a competing product or service.
      </Paragraph>

      <SectionTitle>6. Termination</SectionTitle>
      <Paragraph>
        6.1 CSIR may terminate your access to the Demonstration Software at any
        time, for any reason, with or without notice.
      </Paragraph>
      <Paragraph>
        6.2 Upon termination, you must immediately cease all use of the
        Demonstration Software and all CSIR Confidential Information.
      </Paragraph>
      <Paragraph>
        6.3 Your obligations regarding Confidential Information and Intellectual
        Property shall survive termination of this Agreement.
      </Paragraph>

      <SectionTitle>7. Disclaimer of Warranties</SectionTitle>
      <Paragraph>
        7.1 The Demonstration Software is provided "as is" and "with all faults"
        for evaluation purposes only.
      </Paragraph>
      <Paragraph>
        7.2 You acknowledge that the Demonstration Software may contain errors,
        bugs, or other limitations.
      </Paragraph>
      <Paragraph>
        7.3 CSIR makes no warranties, express or implied, including but not
        limited to warranties of merchantability, fitness for a particular
        purpose, or non-infringement.
      </Paragraph>

      <SectionTitle>8. Use of Own Data During Demonstration</SectionTitle>
      <Paragraph>
        8.1 You may use your own or CSIR provided data and/or images during the
        demonstration session to evaluate the Demonstration Software.
      </Paragraph>
      <Paragraph>
        8.2 Where your own data and/or images are used to evaluate the
        Demonstration Software, you do so at your own risk and the CSIR bears no
        liability for the loss or damage of that data and/or images.
      </Paragraph>
      <Paragraph>
        8.3 The CSIR however undertakes to comply with the Protection of
        Personal Information Act 2013, as amended (“the Act”), should any of the
        data and/or images used during the demonstration session meet the
        definition of Personal Information in terms of the Act.
      </Paragraph>

      <SectionTitle>9. Governing Law</SectionTitle>
      <Paragraph>
        This Agreement shall be governed by the laws of the Republic of South
        Africa.
      </Paragraph>
    </TermsContainer>
  );
};

// Styled Components
const TermsContainer = styled.div`
  padding: 40px 20px;
  max-width: 850px;
  margin: 0 auto;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  font-family: "Inter", sans-serif;
  color: #333;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  text-align: center;
  margin-bottom: 5px;
  color: #1a1a1a;
`;

const Subtitle = styled.h3`
  text-align: center;
  font-weight: 500;
  color: #555;
  margin-bottom: 30px;
`;

const Parties = styled.p`
  text-align: center;
  margin-bottom: 40px;
  font-size: 1rem;
  color: #444;
  line-height: 1.6;
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  margin-top: 32px;
  color: #1a1a1a;
  border-left: 4px solid #0077cc;
  padding-left: 12px;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  margin-top: 12px;
  color: #555;
`;